import { IconFromSet } from "@gettactic/components";
import { TacticIconsDictionary } from "@gettactic/components/src/icons/tactic-icons/dictionary";
import { Properties } from "csstype";
import Konva from "konva";
import React from "react";
import ReactDOMServer from "react-dom/server";
import {
  SpotShape,
  SpotShapeType,
  TextShape,
  WallPathShape,
  ZoneShape
} from "../../../types";
import { computeGridCells, computeGridLines } from "../../../utils/gridLayout";
import {
  computeWallPathBounds,
  createFurnitureElement,
  createIconElement,
  createNodeShape,
  createText,
  createWall,
  createWallPath,
  createZone
} from "../../../utils/shapeFactory";
import { snapToWallEndpoint, WallSnapResult } from "../../../utils/snap";
import { loadSvgFile } from "../../../utils/svg";
import { splitWallAtPoint } from "../../../utils/wallAttach";
import { computeWallFloorPolygons } from "../../../utils/wallFloorFill";
import { getAllWallSegments } from "../../../utils/wallPathSegments";
import { normalizeZonePolygon } from "../../../utils/zonePolygon";
import {
  useMapCanvas,
  useMapCanvasInteraction,
  useMapCanvasShapes,
  useMapCanvasStage,
  useMapCanvasStagePosition
} from "../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorProps,
  useMapEditorTool,
  useMapEditorUI
} from "../contexts/MapEditorContext";
import { GRID_LINE_COLOR, GRID_LINE_THICKNESS } from "../defines";
import { useSpotDrawing } from "../editor/spot-numbering/useSpotDrawing";
import { generateNextSpotLabels } from "../helpers/generateNextSpotLabels";
import { getShapeRootTarget } from "../helpers/getRootTarget";
import { useAltToolsListener } from "./useAltToolsListener";
import { useSpotLabelIterator } from "./useSpotLabelIterator";

export interface UseMapCanvasHandlersOptions {
  iconToolGhostRef: React.MutableRefObject<Konva.Group | null>;
  stageRef: React.MutableRefObject<Konva.Stage | null>;
  transformerRef: React.MutableRefObject<Konva.Transformer | null>;
  selectedIdsRef: React.MutableRefObject<string[]>;
}

export interface UseMapCanvasHandlersReturn {
  onWheel: (event: Konva.KonvaEventObject<WheelEvent>) => void;
  onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onMouseDown: (event: Konva.KonvaPointerEvent) => void;
  onMouseMove: (event: Konva.KonvaPointerEvent) => void;
  onMouseEnter: () => void;
  onMouseUp: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onMouseLeave: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onClick: (event: Konva.KonvaPointerEvent) => void;
  onTap: (event: Konva.KonvaPointerEvent) => void;
  handleMultiDragStart: () => void;
  handleMultiDragMove: (event: Konva.KonvaEventObject<DragEvent>) => void;
  handleMultiDragEnd: () => void;
  isPanToolActive: boolean;
  isMouseIn: boolean;
  isDrawing: boolean;
  drawingPoints: number[];
  cursor: Properties["cursor"];
  /** Set right after the measure tool's second click — the pixel length is
   *  known, waiting on the user to enter its real-world length. Drives an
   *  in-app prompt instead of a blocking `window.prompt`. */
  pendingMeasurement: { pixelLength: number } | null;
  confirmMeasurement: (meters: number) => void;
  cancelMeasurement: () => void;
  /** Set once the grid tool's bounding rectangle has been dragged out —
   *  drives the GridPrompt panel asking for rows/columns/spacing/type. */
  pendingGrid: { x: number; y: number; width: number; height: number } | null;
  confirmGrid: (config: {
    rows: number;
    columns: number;
    gap: number;
    spotType: SpotShapeType;
    addSpots: boolean;
  }) => void;
  cancelGrid: () => void;
}

export const useMapEditorHandlers = (
  options: UseMapCanvasHandlersOptions
): UseMapCanvasHandlersReturn => {
  const { iconToolGhostRef, stageRef, selectedIdsRef } = options;

  const {
    drawTool,
    activeTool: tool,
    furnitureToolSelectedFurniture,
    iconToolSelectedIcon
  } = useMapEditorTool();
  const {
    handleDrawStart: onDrawStart,
    handleDrawEnd: onDrawEnd,
    updateShape: onShapeUpdate,
    setSelectedIds: onSelectionChange,
    shouldPreventDeselect,
    setPreviewShape,
    setHighlightedShape,
    setShapes,
    setActiveTool
  } = useMapEditorActions();
  const { highlightedShape, snapToGrid, isDirtyRef } = useMapEditorUI();
  const { restrictedMode } = useMapEditorProps();
  const { shapes } = useMapCanvasShapes();

  const {
    editingZoneWidth,
    editingZoneHeight,
    lastSpotLabel,
    setLastSpotLabel,
    spotLabelPattern,
    gridSize,
    mapIcons,
    setPixelsPerMeter
  } = useMapCanvas();

  const { stagePosition } = useMapCanvasStagePosition();
  const {
    stagePositionRef,
    updateStagePosition: onStageChange,
    setStagePosition,
    minScale,
    maxScale
  } = useMapCanvasStage();

  const {
    minSelectionDistance,
    selectionBox,
    selectionStart,
    isDragging,
    shapesInSelection,
    dragStartPositions,
    setSelectionBox,
    setSelectionStart,
    setIsDragging,
    setShapesInSelection,
    setDragStartPositions
  } = useMapCanvasInteraction();

  const wheelTimeoutRef = React.useRef<number | null>(null);

  const snapValue = (value: number): number => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  const snapPoint = (point: {
    x: number;
    y: number;
  }): { x: number; y: number } => {
    if (!snapToGrid) return point;
    return { x: snapValue(point.x), y: snapValue(point.y) };
  };

  const spotLabel = useSpotLabelIterator();

  const { isAltPaneActive } = useAltToolsListener();

  const isSelecting = React.useRef(false);
  const justFinishedDrag = React.useRef(false);

  // Wall-path drawing state: only the last committed vertex (for preview line)
  const wallPathVerticesRef = React.useRef<{ x: number; y: number }[]>([]);
  // Track the ID of the wall-path shape being drawn incrementally
  const wallPathActiveShapeIdRef = React.useRef<string | null>(null);
  // Spot whose availability-state area is being drawn ('state-area' tool).
  // Captured on the first click so a selection change mid-draw can't
  // redirect vertices to a different spot.
  const stateAreaTargetIdRef = React.useRef<string | null>(null);
  // Zone whose polygon outline is being drawn ('zone-polygon' tool).
  const zonePolygonTargetIdRef = React.useRef<string | null>(null);

  const [isMouseIn, setIsMouseIn] = React.useState(false);

  // Measure tool: set right after the second click, resolved by the panel
  // UI (MeasurementPrompt) reading this back out of the hook instead of a
  // blocking window.prompt.
  const [pendingMeasurement, setPendingMeasurement] = React.useState<{
    pixelLength: number;
  } | null>(null);
  const confirmMeasurement = React.useCallback(
    (meters: number) => {
      setPendingMeasurement((current) => {
        if (current && Number.isFinite(meters) && meters > 0) {
          setPixelsPerMeter(current.pixelLength / meters);
        }
        return null;
      });
    },
    [setPixelsPerMeter]
  );
  const cancelMeasurement = React.useCallback(() => {
    setPendingMeasurement(null);
  }, []);

  // Grid tool: set once the bounding rectangle has been dragged out,
  // resolved by the GridPrompt panel UI reading this back out of the hook
  // (same pattern as pendingMeasurement).
  const [pendingGrid, setPendingGrid] = React.useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const confirmGrid = React.useCallback(
    (config: {
      rows: number;
      columns: number;
      gap: number;
      spotType: SpotShapeType;
      addSpots: boolean;
    }) => {
      // Reads `pendingGrid` directly and calls `setShapes` here, rather than
      // from inside a `setPendingGrid(current => ...)` updater, because that
      // updater isn't pure: it was appending to `shapes` as a side effect.
      // React Strict Mode intentionally invokes state updaters twice in dev
      // to catch exactly this, and it did — each invocation minted
      // freshly-id'd shapes at the same geometry, so `setShapes` ran twice
      // and every grid line/spot got created in duplicate, stacked exactly
      // on top of each other.
      const current = pendingGrid;
      if (current) {
        const { rows, columns, gap, spotType, addSpots } = config;
        const gridConfig = { rows, columns, gap };
        const lines = computeGridLines(current, gridConfig);
        if (lines.length > 0) {
          // One straight WallPathShape per row/column boundary, including the
          // outer edges — thin enough to read as paint rather than a physical
          // wall, and each edge is its own shape so any single one (including
          // an outer perimeter side) can be selected and hidden/deleted later.
          const lineShapes: WallPathShape[] = lines.map((line) => {
            const path = createWallPath(line.vertices) as WallPathShape;
            path.thickness = GRID_LINE_THICKNESS;
            // The editor's layer order is fixed (shapes below spots), so
            // these lines always render underneath the generated spots — a
            // darker, more saturated color than the default pale wall gray
            // keeps them legible through a semi-transparent spot fill.
            path.color = GRID_LINE_COLOR;
            // A dense grid of these lines isn't a room — without this, the
            // floor-fill room detector paints solid white across the whole
            // grid, hiding whatever's underneath regardless of spot opacity.
            path.excludeFromFloorFill = true;
            return path;
          });

          const spotShapes: SpotShape[] = addSpots
            ? computeGridCells(current, gridConfig).map((cell) => {
                const label = spotLabel.next(true).join("");
                const shape = createNodeShape({
                  x: cell.x,
                  y: cell.y,
                  text: label
                }) as SpotShape;
                shape.width = cell.width;
                shape.height = cell.height;
                shape.spotType = spotType;
                const isParkingCell =
                  spotType === "parking_lot" || spotType === "parking_space";
                // Matches the spot panel's own default: a parking spot's own
                // base dot should read as a plain Node, not fall back to the
                // parking look its own type already implies. This only
                // affects the spot's own silhouette — the state-area rect
                // below is a separate, independent overlay, so it still
                // paints the full cell for parking too.
                if (isParkingCell) {
                  shape.spotAppearance = SpotShapeType.Node;
                }
                // The whole cell is the availability indicator — a dot in the
                // corner of a tightly-packed grid doesn't read well, and the
                // point of the grid tool is a painted-lot look. An explicit
                // custom rect (not 'spot-rect' or wall-detected) matching the
                // cell exactly: "Detect Room from Walls" traces enclosed wall
                // faces, which doesn't work here since the grid's dividers are
                // independent line segments, not a walled-off room per cell.
                // Applies to every grid spot type, including parking: the
                // state-area rect is independent of the spot's own base
                // appearance (rendered separately from the Node-styled dot
                // above), so it can't reintroduce the base "gray square"
                // silhouette bug — it's only responsible for the cell's
                // painted-lot fill.
                shape.stateArea = {
                  kind: "rect",
                  hideDot: true,
                  rect: {
                    x: 0,
                    y: 0,
                    width: cell.width,
                    height: cell.height,
                    cornerRadius: 0
                  }
                };
                // Transparent enough that the grid lines drawn underneath
                // (and the floor plan beyond them) stay clearly visible —
                // the spot layer always renders above the lines' layer, so
                // this can't rely on the lines showing through faintly.
                shape.rectProps = {
                  ...shape.rectProps,
                  cornerRadius: 0,
                  opacity: 0.35
                };
                return shape;
              })
            : [];

          setShapes((prev) => [...prev, ...lineShapes, ...spotShapes]);
          setPendingGrid(null);
        }
      }
      setActiveTool("select");
    },
    [pendingGrid, setShapes, setActiveTool, spotLabel]
  );
  const cancelGrid = React.useCallback(() => {
    setPendingGrid(null);
  }, []);

  const isPanToolActive = tool === "pan" || isAltPaneActive;

  const isGhostItemEnabled =
    (tool === "icon" && iconToolSelectedIcon) ||
    (tool === "furniture" && furnitureToolSelectedFurniture) ||
    tool === "spot";

  const cursor = isPanToolActive
    ? "grab"
    : tool === "draw" ||
        tool === "numbering" ||
        tool === "draw-select" ||
        tool === "zone" ||
        tool === "wall" ||
        tool === "wall-rect" ||
        tool === "wall-path" ||
        tool === "state-area" ||
        tool === "zone-polygon" ||
        tool === "measure" ||
        tool === "auto-zone" ||
        tool === "grid"
      ? "crosshair"
      : tool === "text"
        ? "text"
        : tool === "node-style"
          ? "cell"
          : "default";
  const {
    isDrawing,
    points: drawingPoints,
    handlers: drawSelectHandlers
  } = useSpotDrawing({
    stagePosition,
    onDrawEnd: (hitSpots, activeTool) => {
      const editedShapes: Record<string, SpotShape> = {};
      const selectedIds: string[] = [];

      let lastSpotNumber: string[] | null = lastSpotLabel || [];

      hitSpots.forEach(({ id }) => {
        if (activeTool === "draw-select") {
          selectedIds.push(id);
          return;
        }
        const shape = shapes.find((s) => {
          return s.id === id;
        });

        if (shape?.type === "spot") {
          if (!editedShapes[shape.id]) editedShapes[shape.id] = shape;

          lastSpotNumber = generateNextSpotLabels(
            spotLabelPattern,
            lastSpotNumber || []
          );

          if (lastSpotNumber)
            editedShapes[shape.id].label = lastSpotNumber.join("");
        }
      });

      setLastSpotLabel(lastSpotNumber);

      if (activeTool === "draw-select") {
        onSelectionChange(selectedIds);
        return;
      }

      Object.entries(editedShapes).forEach(([id, shape]) => {
        onShapeUpdate(id, shape);
      });
    }
  });

  /** Whether the current selection drag uses spot-only filtering (Ctrl/Cmd held) */
  const selectionSpotsOnly = React.useRef(false);

  const updateShapesInSelection = React.useCallback(
    (selectionBox: { x: number; y: number; width: number; height: number }) => {
      const boxRight = selectionBox.x + selectionBox.width;
      const boxBottom = selectionBox.y + selectionBox.height;

      // Check if a thick line segment intersects a rectangle.
      // Expands the rect by half-thickness and tests line-rect intersection.
      const segmentIntersectsBox = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        half: number,
        bx: number,
        by: number,
        bRight: number,
        bBottom: number
      ): boolean => {
        // Expand box by half-thickness to account for wall stroke width
        const ex = bx - half;
        const ey = by - half;
        const eRight = bRight + half;
        const eBottom = bBottom + half;

        // Quick reject: segment bounding box vs expanded box
        if (Math.max(x1, x2) < ex || Math.min(x1, x2) > eRight) return false;
        if (Math.max(y1, y2) < ey || Math.min(y1, y2) > eBottom) return false;

        // If either endpoint is inside the expanded box, it intersects
        if (x1 >= ex && x1 <= eRight && y1 >= ey && y1 <= eBottom) return true;
        if (x2 >= ex && x2 <= eRight && y2 >= ey && y2 <= eBottom) return true;

        // Test line against the 4 edges of the expanded box
        const edges: [number, number, number, number][] = [
          [ex, ey, eRight, ey], // top
          [ex, eBottom, eRight, eBottom], // bottom
          [ex, ey, ex, eBottom], // left
          [eRight, ey, eRight, eBottom] // right
        ];

        for (const [ax, ay, bx2, by2] of edges) {
          // Check if two line segments intersect using cross products
          const d1x = x2 - x1,
            d1y = y2 - y1;
          const d2x = bx2 - ax,
            d2y = by2 - ay;
          const cross = d1x * d2y - d1y * d2x;
          if (Math.abs(cross) < 1e-10) continue;
          const t = ((ax - x1) * d2y - (ay - y1) * d2x) / cross;
          const u = ((ax - x1) * d1y - (ay - y1) * d1x) / cross;
          if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return true;
        }

        return false;
      };

      const selectedShapes = shapes.filter((shape) => {
        if (shape.locked || shape.hidden) return false;
        if (selectionSpotsOnly.current && shape.type !== "spot") return false;

        // Compute axis-aligned bounding box depending on shape type
        let left: number;
        let top: number;
        let right: number;
        let bottom: number;

        if (shape.type === "circle") {
          // Circle: x,y is center; width/height are radii
          left = shape.x - shape.width;
          top = shape.y - shape.height;
          right = shape.x + shape.width;
          bottom = shape.y + shape.height;
        } else if (shape.type === "wall") {
          // Test actual segment geometry, not just bounding box
          const half = (shape.thickness || 10) / 2;
          return segmentIntersectsBox(
            shape.x,
            shape.y,
            shape.x2,
            shape.y2,
            half,
            selectionBox.x,
            selectionBox.y,
            boxRight,
            boxBottom
          );
        } else if (shape.type === "wall-path") {
          // Test each edge segment individually
          const half = (shape.thickness || 10) / 2;
          const { vertices, edges, closed } = shape;
          const edgeCount = closed ? vertices.length : vertices.length - 1;
          for (let i = 0; i < edgeCount; i++) {
            const from = vertices[i];
            const to = vertices[(i + 1) % vertices.length];
            if (
              segmentIntersectsBox(
                from.x,
                from.y,
                to.x,
                to.y,
                half,
                selectionBox.x,
                selectionBox.y,
                boxRight,
                boxBottom
              )
            ) {
              return true;
            }
          }
          return false;
        } else if (shape.rotation) {
          // Compute AABB of rotated shape corners
          const rad = (shape.rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);
          const cx = shape.x;
          const cy = shape.y;
          // Corners relative to origin (shape.x, shape.y)
          const corners = [
            [0, 0],
            [shape.width, 0],
            [shape.width, shape.height],
            [0, shape.height]
          ];
          const xs = corners.map(([dx, dy]) => cx + cos * dx - sin * dy);
          const ys = corners.map(([dx, dy]) => cy + sin * dx + cos * dy);
          left = Math.min(...xs);
          top = Math.min(...ys);
          right = Math.max(...xs);
          bottom = Math.max(...ys);
        } else {
          left = shape.x;
          top = shape.y;
          right = shape.x + shape.width;
          bottom = shape.y + shape.height;
        }

        return (
          left < boxRight &&
          right > selectionBox.x &&
          top < boxBottom &&
          bottom > selectionBox.y
        );
      });

      setShapesInSelection(selectedShapes.map((shape) => shape.id));
    },
    [shapes, setShapesInSelection]
  );

  const updateIconToolGhostPosition = (position?: {
    x: number;
    y: number;
    scale: number;
  }) => {
    if (isGhostItemEnabled && iconToolGhostRef.current) {
      const stage = stageRef.current;
      const pos = stage?.getPointerPosition();
      if (!pos || !stage) return;

      // Adjust position based on scale and stage offset
      const scale = position ? position.scale : stage.scaleX(); // Assuming scaleX and scaleY are the same
      const newPos = {
        x: (pos.x - (position ? position.x : stage.x())) / scale,
        y: (pos.y - (position ? position.y : stage.y())) / scale
      };

      // Set the position of the ghost element
      iconToolGhostRef.current.position(newPos);

      // The spot ghost renders at true world scale — the same size the
      // placed desk will actually be — so aiming with it is accurate at
      // any zoom level. Icon/furniture ghosts keep a constant on-screen
      // size regardless of zoom instead: those source assets are often
      // much smaller than a spot and would be hard to see zoomed out.
      const ghostScale = tool === "spot" ? 1 : 1 / scale;
      iconToolGhostRef.current.scale({
        x: ghostScale,
        y: ghostScale
      });

      iconToolGhostRef.current.getLayer()?.batchDraw();
    }
  };

  /** Stop wall-path drawing. The shape already exists on the canvas (committed incrementally). */
  const stopWallPathDrawing = (closed?: boolean) => {
    if (closed && wallPathActiveShapeIdRef.current) {
      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== wallPathActiveShapeIdRef.current) return s;
          const path = s as WallPathShape;
          const closingEdge = {};
          return {
            ...path,
            closed: true,
            edges: [...path.edges, closingEdge]
          };
        })
      );
    }
    wallPathVerticesRef.current = [];
    wallPathActiveShapeIdRef.current = null;
    setPreviewShape(null);
  };

  /** Stop drawing a zone's polygon outline. With 3+ corners the outline is
   *  re-anchored to its bbox; fewer corners revert the zone to its
   *  rectangle. Returns to the select tool. */
  const finishZonePolygonDrawing = () => {
    const targetId = zonePolygonTargetIdRef.current;
    if (targetId) {
      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== targetId || s.type !== "zone") return s;
          const zone = s as ZoneShape;
          const vertices = zone.vertices || [];
          if (vertices.length < 3) {
            return { ...zone, vertices: undefined, edges: undefined };
          }
          return {
            ...zone,
            ...normalizeZonePolygon(
              zone,
              vertices,
              vertices.map(() => ({}))
            )
          };
        })
      );
    }
    zonePolygonTargetIdRef.current = null;
    setActiveTool("select");
  };

  /** Stop drawing a spot's availability-state polygon. Drops the area if it
   *  has fewer than 3 corners, then returns to the select tool. */
  const finishStateAreaDrawing = () => {
    const targetId = stateAreaTargetIdRef.current;
    if (targetId) {
      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== targetId) return s;
          const spot = s as SpotShape;
          if (
            spot.stateArea?.kind === "polygon" &&
            (spot.stateArea.points?.length ?? 0) < 3
          ) {
            return { ...spot, stateArea: undefined };
          }
          return s;
        })
      );
    }
    stateAreaTargetIdRef.current = null;
    setActiveTool("select");
  };

  const handleWheel = React.useCallback(
    (event: Konva.KonvaEventObject<WheelEvent>) => {
      event.evt.preventDefault();

      const stagePosition = {
        x: stageRef.current?.x() || 0,
        y: stageRef.current?.y() || 0,
        scale: stageRef.current?.scaleX() || 1
      };

      const stage = stageRef.current;
      if (!stage) return;

      if (event.evt.metaKey || isPanToolActive) {
        const oldScale = stagePosition.scale;
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        const mousePointTo = {
          x: (pointer.x - stagePosition.x) / oldScale,
          y: (pointer.y - stagePosition.y) / oldScale
        };

        const newScale = event.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
        const boundedScale = Math.min(Math.max(newScale, 0.1), maxScale);

        if (boundedScale !== stagePosition.scale) {
          const newPos = {
            x: pointer.x - mousePointTo.x * boundedScale,
            y: pointer.y - mousePointTo.y * boundedScale,
            scale: boundedScale
          };
          stage.setPosition(newPos);
          stage.scale({
            x: boundedScale,
            y: boundedScale
          });

          if (wheelTimeoutRef.current) {
            clearTimeout(wheelTimeoutRef.current);
          }

          wheelTimeoutRef.current = window.setTimeout(() => {
            onStageChange(newPos);
          }, 100);

          if (isGhostItemEnabled) {
            updateIconToolGhostPosition(newPos);
          }
        }
      } else {
        const panIntensity = 1 / Math.max(stagePosition.scale, 0.5);
        const newPos = {
          x: stagePosition.x - event.evt.deltaX * panIntensity,
          y: stagePosition.y - event.evt.deltaY * panIntensity,
          scale: stagePosition.scale
        };

        stage.setPosition(newPos);

        if (wheelTimeoutRef.current) {
          clearTimeout(wheelTimeoutRef.current);
        }

        wheelTimeoutRef.current = window.setTimeout(() => {
          onStageChange(newPos);
        }, 100);

        if (isGhostItemEnabled) {
          updateIconToolGhostPosition(newPos);
        }
      }
    },
    [
      stagePosition,
      onStageChange,
      tool,
      editingZoneWidth,
      editingZoneHeight,
      isGhostItemEnabled
    ]
  );

  const handleDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (isPanToolActive) {
      const newPos = event.target.position();
      onStageChange({
        x: newPos.x,
        y: newPos.y,
        scale: stagePosition.scale
      });
    }
  };

  const handleMouseDown = (event: Konva.KonvaPointerEvent) => {
    if (isPanToolActive) {
      return;
    }

    if (tool === "numbering" || tool === "draw-select") {
      drawSelectHandlers.onMouseDown(event);
      return;
    }

    if (tool === "draw" || tool === "zone" || tool === "grid") {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });
      onDrawStart(transformedPoint);
      return;
    }

    if (tool === "wall") {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const raw = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });

      if (!drawTool.startPoint) {
        // Snap to existing wall endpoints or segment interiors.
        // Holding Alt bypasses wall snapping for free placement.
        const snapped: WallSnapResult = event.evt.altKey
          ? { ...raw, snapped: false }
          : snapToWallEndpoint(raw, shapes, stagePosition.scale, undefined, {
              includeSegments: true
            });
        const transformed = { x: snapped.x, y: snapped.y };
        // Starting on the interior of an existing wall: split it so the
        // start point becomes a real vertex (T-junction)
        if (snapped.segment) {
          const segment = snapped.segment;
          setShapes((prev) =>
            splitWallAtPoint(prev, { ...segment, ...transformed })
          );
        }
        onDrawStart(transformed);
        return;
      }

      // Shift key: constrain to the nearest 0/45/90° angle from the start
      // point FIRST, then look for a wall to connect to along that
      // constrained line — so holding Shift keeps the new wall straight
      // even when finishing close to another wall, instead of the
      // connect-snap silently overriding the angle constraint (matching
      // the preview behavior in handleMouseMove).
      let candidate = raw;
      if (event.evt.shiftKey) {
        const dx = raw.x - drawTool.startPoint.x;
        const dy = raw.y - drawTool.startPoint.y;
        const angle = Math.atan2(dy, dx);
        const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        const dist = Math.sqrt(dx * dx + dy * dy);
        candidate = {
          x: drawTool.startPoint.x + dist * Math.cos(snappedAngle),
          y: drawTool.startPoint.y + dist * Math.sin(snappedAngle)
        };
      }

      const snapped: WallSnapResult = event.evt.altKey
        ? { ...candidate, snapped: false }
        : snapToWallEndpoint(
            candidate,
            shapes,
            stagePosition.scale,
            undefined,
            {
              includeSegments: true
            }
          );
      const endX = snapped.x;
      const endY = snapped.y;

      const segment = snapped.segment;
      const newShape = createWall(drawTool.startPoint, {
        x: endX,
        y: endY
      });
      setShapes((prev) => {
        // Ending on the interior of an existing wall: split the host first
        const next = segment
          ? splitWallAtPoint(prev, { ...segment, x: endX, y: endY })
          : prev;
        return [...next, newShape];
      });
      // Clear current draw state
      onDrawEnd(drawTool.startPoint, { x: endX, y: endY }, "star");

      if (event.evt.shiftKey) {
        // Chain mode: start a new wall from the endpoint
        onDrawStart({ x: endX, y: endY });
      } else {
        setPreviewShape(null);
      }
      return;
    }

    if (tool === "wall-rect") {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });

      if (!drawTool.startPoint) {
        onDrawStart(transformed);
      } else {
        const x1 = drawTool.startPoint.x;
        const y1 = drawTool.startPoint.y;
        const x2 = transformed.x;
        const y2 = transformed.y;

        // Create a single closed WallPathShape forming a rectangle
        const topLeft = { x: Math.min(x1, x2), y: Math.min(y1, y2) };
        const topRight = { x: Math.max(x1, x2), y: Math.min(y1, y2) };
        const bottomRight = { x: Math.max(x1, x2), y: Math.max(y1, y2) };
        const bottomLeft = { x: Math.min(x1, x2), y: Math.max(y1, y2) };

        const wallPath = createWallPath(
          [topLeft, topRight, bottomRight, bottomLeft],
          { closed: true }
        );

        setShapes((prev) => [...prev, wallPath]);
        onDrawEnd(drawTool.startPoint, transformed, "star");
        setPreviewShape(null);
      }
      return;
    }

    if (tool === "zone-polygon") {
      // Draw a zone's polygon outline: each click adds a corner (committed
      // straight to the zone so the polygon renders live); double-click,
      // right-click or clicking the first corner finishes.
      const targetId =
        zonePolygonTargetIdRef.current ?? selectedIdsRef.current[0] ?? null;
      const target = shapes.find(
        (s): s is ZoneShape => s.id === targetId && s.type === "zone"
      );
      if (!target) {
        zonePolygonTargetIdRef.current = null;
        setActiveTool("select");
        return;
      }
      zonePolygonTargetIdRef.current = target.id;

      if (event.evt.button === 2) {
        event.evt.preventDefault();
        finishZonePolygonDrawing();
        return;
      }
      if (event.evt.detail >= 2) {
        finishZonePolygonDrawing();
        return;
      }

      const stage = stageRef.current;
      if (!stage) return;
      const pos = stage.getPointerPosition();
      if (!pos) return;

      const world = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });
      // Zone vertices are relative to the zone origin. Re-anchoring to the
      // bbox happens once at finish so mid-draw clicks keep a stable frame.
      const localPoint = {
        x: Math.round(world.x - target.x),
        y: Math.round(world.y - target.y)
      };

      const currentPoints = target.vertices || [];

      // Clicking near the first corner closes the polygon
      if (currentPoints.length >= 2) {
        const first = currentPoints[0];
        const closeDist = Math.hypot(
          localPoint.x - first.x,
          localPoint.y - first.y
        );
        const closeThreshold = 15 / Math.max(stagePosition.scale, 0.1);
        if (closeDist < closeThreshold) {
          finishZonePolygonDrawing();
          return;
        }
      }

      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== target.id || s.type !== "zone") return s;
          const zone = s as ZoneShape;
          const vertices = [...(zone.vertices || []), localPoint];
          return { ...zone, vertices, edges: vertices.map(() => ({})) };
        })
      );
      return;
    }

    if (tool === "measure") {
      // Two-click flow: first click anchors the start point (preview line
      // follows the cursor from there, see handleMouseMove); second click
      // measures the pixel distance and prompts for its real-world length
      // to calibrate the map's scale. Always returns to select afterward.
      const stage = stageRef.current;
      if (!stage) return;
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const transformed = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });

      if (!drawTool.startPoint) {
        onDrawStart(transformed);
        return;
      }

      const pixelLength = Math.hypot(
        transformed.x - drawTool.startPoint.x,
        transformed.y - drawTool.startPoint.y
      );
      // "star" is a no-op shape type for handleDrawEnd — it only resets
      // drawTool.startPoint, same as the wall tool's chained-segment reset.
      onDrawEnd(drawTool.startPoint, transformed, "star");
      setPreviewShape(null);
      setActiveTool("select");

      if (pixelLength < 1) return;
      // Real-world length is collected via the in-app MeasurementPrompt
      // (rendered by MapEditorCanvas off pendingMeasurement) instead of a
      // blocking window.prompt.
      setPendingMeasurement({ pixelLength });
      return;
    }

    if (tool === "auto-zone") {
      // Materialize a wall-graph-detected room (rendered as a ghost polygon
      // by AutoZoneOverlay, which tags each with a `roomIndex` attr) into a
      // real polygon zone. Recomputing here instead of reading from the
      // overlay keeps this tool stateless — cheap enough since it only
      // runs on an actual click, not per frame, and only requires shapes
      // being unchanged since the overlay's last render (true for a click).
      const roomIndex = (event.target as Konva.Shape).attrs?.roomIndex;
      if (roomIndex === undefined) return;
      const walls = getAllWallSegments(shapes);
      const polygons = computeWallFloorPolygons(walls);
      const polygon = polygons[roomIndex];
      if (!polygon || polygon.points.length < 3) return;

      const roomCount = shapes.filter((s) => s.type === "zone").length + 1;
      const base = createZone({ x: 0, y: 0 }) as ZoneShape;
      const normalized = normalizeZonePolygon(
        { x: 0, y: 0 },
        polygon.points,
        polygon.points.map(() => ({}))
      );
      const newZone: ZoneShape = {
        ...base,
        ...normalized,
        name: `Room ${roomCount}`
      } as ZoneShape;

      setShapes((prev) => [...prev, newZone]);
      // Stay on the tool so the user can stamp multiple detected rooms
      // in one pass, matching the zone-polygon/state-area draw tools.
      return;
    }

    if (tool === "state-area") {
      // Draw a spot's availability-state polygon: each click adds a corner
      // (committed straight to the spot's stateArea so the dashed editor
      // preview gives live feedback); double-click, right-click or clicking
      // the first corner finishes.
      const targetId =
        stateAreaTargetIdRef.current ?? selectedIdsRef.current[0] ?? null;
      const target = shapes.find(
        (s): s is SpotShape => s.id === targetId && s.type === "spot"
      );
      if (!target) {
        stateAreaTargetIdRef.current = null;
        setActiveTool("select");
        return;
      }
      stateAreaTargetIdRef.current = target.id;

      if (event.evt.button === 2) {
        event.evt.preventDefault();
        finishStateAreaDrawing();
        return;
      }
      if (event.evt.detail >= 2) {
        finishStateAreaDrawing();
        return;
      }

      const stage = stageRef.current;
      if (!stage) return;
      const pos = stage.getPointerPosition();
      if (!pos) return;

      const world = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });

      // Convert to the spot's local frame (inverse of its rotation).
      const radians = ((target.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);
      const dx = world.x - target.x;
      const dy = world.y - target.y;
      const localPoint = {
        x: dx * cos + dy * sin,
        y: -dx * sin + dy * cos
      };

      const currentPoints =
        target.stateArea?.kind === "polygon"
          ? target.stateArea.points || []
          : [];

      // Clicking near the first corner closes the polygon
      if (currentPoints.length >= 2) {
        const first = currentPoints[0];
        const closeDist = Math.hypot(
          localPoint.x - first.x,
          localPoint.y - first.y
        );
        const closeThreshold = 15 / Math.max(stagePosition.scale, 0.1);
        if (closeDist < closeThreshold) {
          finishStateAreaDrawing();
          return;
        }
      }

      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== target.id) return s;
          const spot = s as SpotShape;
          return {
            ...spot,
            stateArea: {
              kind: "polygon",
              hideDot: spot.stateArea?.hideDot,
              points: [...currentPoints, localPoint]
            }
          };
        })
      );
      return;
    }

    if (tool === "wall-path") {
      // Right-click stops drawing
      if (event.evt.button === 2) {
        event.evt.preventDefault();
        stopWallPathDrawing();
        return;
      }

      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const raw = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });

      const existingVerts = wallPathVerticesRef.current;

      // Shift key: constrain to the nearest 0/45/90° angle from the last
      // vertex FIRST, then look for a wall to connect to along that
      // constrained line — so holding Shift keeps the new segment
      // straight even when finishing close to another wall, instead of
      // the connect-snap silently overriding the angle constraint
      // (matching the preview behavior in handleMouseMove).
      let candidate = raw;
      if (event.evt.shiftKey && existingVerts.length > 0) {
        const last = existingVerts[existingVerts.length - 1];
        const dx = raw.x - last.x;
        const dy = raw.y - last.y;
        const angle = Math.atan2(dy, dx);
        const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        const dist = Math.sqrt(dx * dx + dy * dy);
        candidate = {
          x: last.x + dist * Math.cos(snappedAngle),
          y: last.y + dist * Math.sin(snappedAngle)
        };
      }

      // Snap to existing wall endpoints or segment interiors (excluding the
      // path being drawn, so it can't split itself mid-session).
      // Holding Alt bypasses wall snapping for free placement.
      const snapped: WallSnapResult = event.evt.altKey
        ? { ...candidate, snapped: false }
        : snapToWallEndpoint(
            candidate,
            shapes,
            stagePosition.scale,
            existingVerts,
            {
              includeSegments: true,
              excludeSegmentShapeId:
                wallPathActiveShapeIdRef.current ?? undefined
            }
          );
      const endX = snapped.x;
      const endY = snapped.y;

      const newVertex = { x: endX, y: endY };
      // Landing on the interior of an existing wall: that wall gets split so
      // the clicked point becomes a shared vertex (T-junction)
      const attachSegment = snapped.segment;

      // Check for close: click near first vertex of the active shape to close
      // path (skipped while Alt is held, so vertices can land near the start)
      if (wallPathActiveShapeIdRef.current && !event.evt.altKey) {
        const activeShape = shapes.find(
          (s) => s.id === wallPathActiveShapeIdRef.current
        ) as WallPathShape | undefined;
        if (activeShape && activeShape.vertices.length >= 2) {
          const first = activeShape.vertices[0];
          const closeDist = Math.sqrt(
            (endX - first.x) ** 2 + (endY - first.y) ** 2
          );
          const closeThreshold = 15 / Math.max(stagePosition.scale, 0.1);
          if (closeDist < closeThreshold) {
            stopWallPathDrawing(true);
            return;
          }
        }
      }

      // Double-click stops drawing
      if (event.evt.detail >= 2 && wallPathActiveShapeIdRef.current) {
        stopWallPathDrawing();
        return;
      }

      // First click — just store the first vertex
      if (existingVerts.length === 0) {
        if (attachSegment) {
          setShapes((prev) =>
            splitWallAtPoint(prev, { ...attachSegment, ...newVertex })
          );
        }
        wallPathVerticesRef.current = [newVertex];
        return;
      }

      // Second click — create the wall-path shape with 2 vertices
      if (!wallPathActiveShapeIdRef.current) {
        const allVerts = [...existingVerts, newVertex];
        const newShape = createWallPath(allVerts, { closed: false });
        setShapes((prev) => {
          const next = attachSegment
            ? splitWallAtPoint(prev, { ...attachSegment, ...newVertex })
            : prev;
          return [...next, newShape];
        });
        wallPathActiveShapeIdRef.current = newShape.id;
        wallPathVerticesRef.current = [newVertex];
        return;
      }

      // Subsequent clicks — append vertex to the existing shape
      setShapes((prev) => {
        const next = attachSegment
          ? splitWallAtPoint(prev, { ...attachSegment, ...newVertex })
          : prev;
        return next.map((s) => {
          if (s.id !== wallPathActiveShapeIdRef.current) return s;
          const path = s as WallPathShape;
          const newVerts = [...path.vertices, newVertex];
          const newEdges = [...path.edges, {}];
          const bounds = computeWallPathBounds(newVerts);
          return {
            ...path,
            vertices: newVerts,
            edges: newEdges,
            x: bounds.x,
            y: bounds.y,
            width: bounds.width,
            height: bounds.height
          };
        });
      });
      wallPathVerticesRef.current = [newVertex];
      return;
    }

    if (tool === "spot") {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = snapPoint({
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      });
      const newShape = createNodeShape({
        ...transformed,
        text: spotLabel.next(true).join("")
      }) as SpotShape;
      newShape.layerType = "spots";
      setShapes((prev) => [...prev, newShape]);

      // Restricted mode is a single-desk flow — placing more than one spot
      // fails validation on save, so switch back to the selection tool and
      // select the new desk immediately instead of leaving the user in
      // spot-placement mode (where the next click would create another one).
      if (restrictedMode) {
        setActiveTool("select");
        onSelectionChange([newShape.id]);
      }
    }

    if (tool === "text") {
      const stage = stageRef.current;
      if (!stage) return;

      const clickOnText = event.target.attrs.id.startsWith("shape-text");
      if (clickOnText || highlightedShape?.shape.type === "text") {
        if (clickOnText) {
          const textShapeId = event.target.attrs.id.replace(/^shape-/, "");
          const shape = shapes.find((s) => s.id === textShapeId);
          if (shape) {
            setHighlightedShape({
              shape,
              targetId: shape.id
            });
          }
        } else {
          setHighlightedShape(null);
        }
        return;
      }

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };
      const newShape = createText(transformed) as TextShape;
      newShape.text = "Text";

      newShape.layerType = "spots";
      setShapes((prev) => [...prev, newShape]);
    }

    if (tool === "icon" && iconToolSelectedIcon) {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };
      let iconText = "";
      let iconUrl: string | undefined;

      // Check if this is an API-sourced icon (merged into tactic set)
      if (mapIcons) {
        const apiIcon = mapIcons.find(
          (i) => i.name === iconToolSelectedIcon.icon && i.category === "icon"
        );
        if (apiIcon) {
          iconUrl = apiIcon.icon_url;
          iconText = iconToolSelectedIcon.icon.toUpperCase();
        }
      }

      if (
        !iconUrl &&
        iconToolSelectedIcon.set === "tactic" &&
        iconToolSelectedIcon.icon in TacticIconsDictionary.tactic.icons
      ) {
        iconText = (
          TacticIconsDictionary.tactic.icons?.[iconToolSelectedIcon.icon] || ""
        ).toUpperCase();
      }

      const place = (width?: number, height?: number, svgContent?: string) => {
        const newShape = createIconElement(
          transformed,
          iconToolSelectedIcon,
          iconText,
          iconUrl,
          width,
          height,
          svgContent
        );
        newShape.layerType = "shapes";
        setShapes((prev) => [...prev, newShape]);
      };

      // Compute the actual content bbox of the icon by mounting it
      // off-screen and calling getBBox(). The SVG's declared viewBox
      // commonly extends past the visible drawing (built-in padding
      // around the figure), so sizing the placed shape from viewBox alone
      // leaves visible whitespace inside the box. Trimming the viewBox to
      // the content bbox eliminates that — the rendered icon then fills
      // the placed box edge-to-edge.
      const trimAndPlace = (rawSvg: string) => {
        let svgEl: SVGSVGElement | null = null;
        let host: HTMLDivElement | null = null;
        try {
          host = document.createElement("div");
          host.style.cssText =
            "position:absolute;left:-10000px;top:-10000px;visibility:hidden;pointer-events:none;";
          host.innerHTML = rawSvg;
          document.body.appendChild(host);
          svgEl = host.querySelector("svg");
          if (!svgEl) {
            place();
            return;
          }
          const bbox = svgEl.getBBox();
          if (bbox.width <= 0 || bbox.height <= 0) {
            place();
            return;
          }
          svgEl.setAttribute(
            "viewBox",
            `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`
          );
          svgEl.removeAttribute("width");
          svgEl.removeAttribute("height");
          const trimmedSvg = svgEl.outerHTML;
          const scale = 100 / Math.max(bbox.width, bbox.height);
          place(bbox.width * scale, bbox.height * scale, trimmedSvg);
        } catch {
          place();
        } finally {
          if (host) document.body.removeChild(host);
        }
      };

      if (iconUrl) {
        fetch(iconUrl)
          .then((r) => r.text())
          .then(trimAndPlace)
          .catch(() => place());
      } else {
        try {
          const svgString = ReactDOMServer.renderToStaticMarkup(
            React.createElement(IconFromSet, {
              icon: iconToolSelectedIcon,
              size: 100
            })
          );
          trimAndPlace(svgString);
        } catch {
          place();
        }
      }
    }

    if (tool === "furniture" && furnitureToolSelectedFurniture) {
      const stage = stageRef.current;
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const transformed = {
        x: (pos.x - stagePosition.x) / stagePosition.scale,
        y: (pos.y - stagePosition.y) / stagePosition.scale
      };

      // Uploaded furniture often comes back from the API with placeholder
      // dimensions that don't match its SVG's viewBox (e.g. 100×100 stored
      // on an asset whose natural box is 144×100). Fetch the SVG up front
      // and let its viewBox set the placement aspect — clamped to 100 on
      // the longest side. Built-in furniture, which already carries
      // correctly-tuned dims, falls back to the existing path.
      const placeFurniture = (width: number, height: number) => {
        const newShape = createFurnitureElement(
          transformed,
          furnitureToolSelectedFurniture
        );
        newShape.layerType = "shapes";
        newShape.width = width;
        newShape.height = height;
        setShapes((prev) => [...prev, newShape]);
      };
      const cw = furnitureToolSelectedFurniture.width;
      const ch = furnitureToolSelectedFurniture.height;
      if (furnitureToolSelectedFurniture.url) {
        loadSvgFile(furnitureToolSelectedFurniture.url)
          .then(({ width: nW, height: nH }) => {
            if (nW > 0 && nH > 0) {
              const naturalAspect = nW / nH;
              const callerAspect = cw && ch ? cw / ch : naturalAspect;
              if (cw && ch && Math.abs(callerAspect - naturalAspect) <= 0.05) {
                placeFurniture(cw, ch);
              } else {
                const target = cw && ch ? Math.max(cw, ch) : Math.max(nW, nH);
                const scale = target / Math.max(nW, nH);
                placeFurniture(Math.round(nW * scale), Math.round(nH * scale));
              }
            } else {
              placeFurniture(cw || 100, ch || 100);
            }
          })
          .catch(() => placeFurniture(cw || 100, ch || 100));
      } else {
        placeFurniture(cw || 100, ch || 100);
      }
    }

    if (tool !== "select" || event.evt.altKey) return;

    const clickedTarget = getShapeRootTarget(
      shapes,
      event.target as Konva.Shape
    );

    const clickedOnShape =
      clickedTarget !== null && clickedTarget.locked !== true;

    // Allow rubber band selection to start through walls/wall-paths,
    // so users can drag-select shapes that sit inside wall enclosures.
    // Walls can still be selected via single click (handled by their onClick).
    const clickedOnWall =
      clickedTarget?.type === "wall" || clickedTarget?.type === "wall-path";

    // If the wall/wall-path is already selected, it becomes draggable —
    // skip rubber-band so the drag isn't interrupted after a few pixels.
    const isSelectedWall =
      clickedOnWall &&
      clickedTarget &&
      selectedIdsRef.current.includes(clickedTarget.id);

    if (clickedOnShape && (!clickedOnWall || isSelectedWall)) {
      isSelecting.current = false;
      return;
    }

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const transformed = {
      x: (pos.x - stagePosition.x) / stagePosition.scale,
      y: (pos.y - stagePosition.y) / stagePosition.scale
    };

    setSelectionStart(transformed);
    setIsDragging(false);
    setSelectionBox(null);
    setShapesInSelection([]);
    isSelecting.current = true;
    selectionSpotsOnly.current = event.evt.ctrlKey || event.evt.metaKey;
  };

  // Add state to track if shift key is pressed
  const [isShiftPressed, setIsShiftPressed] = React.useState(false);

  // Clear wall-path drawing state when switching away from wall-path tool
  React.useEffect(() => {
    if (tool !== "wall-path") {
      if (
        wallPathVerticesRef.current.length > 0 ||
        wallPathActiveShapeIdRef.current
      ) {
        wallPathVerticesRef.current = [];
        wallPathActiveShapeIdRef.current = null;
        setPreviewShape(null);
      }
    }
  }, [tool, setPreviewShape]);

  // Leaving the zone-polygon tool by any other route: finalize like the
  // state-area cleanup below — revert an unfinished outline (<3 corners),
  // re-anchor a finished one, without forcing the tool back to select.
  React.useEffect(() => {
    if (tool === "zone-polygon" || !zonePolygonTargetIdRef.current) return;
    const targetId = zonePolygonTargetIdRef.current;
    zonePolygonTargetIdRef.current = null;
    setShapes((prev) =>
      prev.map((s) => {
        if (s.id !== targetId || s.type !== "zone") return s;
        const zone = s as ZoneShape;
        const vertices = zone.vertices || [];
        if (vertices.length < 3) {
          return { ...zone, vertices: undefined, edges: undefined };
        }
        return {
          ...zone,
          ...normalizeZonePolygon(
            zone,
            vertices,
            vertices.map(() => ({}))
          )
        };
      })
    );
  }, [tool, setShapes]);

  // Leaving the state-area tool by any other route (e.g. picking a toolbar
  // tool mid-draw): drop an unfinished polygon (<3 corners) and release the
  // target — without forcing the tool back to select.
  React.useEffect(() => {
    if (tool === "state-area" || !stateAreaTargetIdRef.current) return;
    const targetId = stateAreaTargetIdRef.current;
    stateAreaTargetIdRef.current = null;
    setShapes((prev) =>
      prev.map((s) => {
        if (s.id !== targetId) return s;
        const spot = s as SpotShape;
        if (
          spot.stateArea?.kind === "polygon" &&
          (spot.stateArea.points?.length ?? 0) < 3
        ) {
          return { ...spot, stateArea: undefined };
        }
        return s;
      })
    );
  }, [tool, setShapes]);

  // Add key event listeners for shift key and escape (wall-path finalize)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setIsShiftPressed(true);
        if ((tool === "draw" || tool === "zone") && drawTool.startPoint) {
          updatePreviewShapeWithShift(true);
        }
      }

      if ((e.key === "Escape" || e.key === "Enter") && tool === "wall-path") {
        stopWallPathDrawing();
      }

      if ((e.key === "Escape" || e.key === "Enter") && tool === "state-area") {
        finishStateAreaDrawing();
      }

      if (
        (e.key === "Escape" || e.key === "Enter") &&
        tool === "zone-polygon"
      ) {
        finishZonePolygonDrawing();
      }

      if (e.key === "Escape" && tool === "measure" && drawTool.startPoint) {
        onDrawEnd(drawTool.startPoint, drawTool.startPoint, "star");
        setPreviewShape(null);
        setActiveTool("select");
      }

      // Tool is already back to 'select' by the time pendingMeasurement is
      // set (the second click resolves the tool immediately), so this is
      // checked independent of `tool`.
      if (e.key === "Escape" && pendingMeasurement) {
        cancelMeasurement();
      }

      if (e.key === "Escape" && tool === "grid" && drawTool.startPoint) {
        onDrawEnd(drawTool.startPoint, drawTool.startPoint, "star");
        setPreviewShape(null);
      }

      if (e.key === "Escape" && pendingGrid) {
        cancelGrid();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setIsShiftPressed(false);
        if ((tool === "draw" || tool === "zone") && drawTool.startPoint) {
          updatePreviewShapeWithShift(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    tool,
    drawTool.startPoint,
    pendingMeasurement,
    cancelMeasurement,
    pendingGrid,
    cancelGrid
  ]);

  // Helper function to update preview shape with shift state
  const updatePreviewShapeWithShift = (shiftPressed: boolean) => {
    if (!drawTool.startPoint) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const transformedPoint = {
      x: (pos.x - stagePosition.x) / stagePosition.scale,
      y: (pos.y - stagePosition.y) / stagePosition.scale
    };

    const width = transformedPoint.x - drawTool.startPoint.x;
    const height = transformedPoint.y - drawTool.startPoint.y;

    // Calculate preview position based on drag direction
    const previewX = width < 0 ? transformedPoint.x : drawTool.startPoint.x;
    const previewY = height < 0 ? transformedPoint.y : drawTool.startPoint.y;

    // If shift key is pressed, make width and height equal
    let finalWidth = Math.abs(width);
    let finalHeight = Math.abs(height);

    if (shiftPressed) {
      // Use the larger dimension to ensure the shape is at least as large as the user's drag
      const maxDimension = Math.max(finalWidth, finalHeight);
      finalWidth = maxDimension;
      finalHeight = maxDimension;
    }

    setPreviewShape({
      x: previewX,
      y: previewY,
      width: finalWidth,
      height: finalHeight,
      type: tool === "zone" ? "zone" : drawTool.selectedShape
    });
  };

  const handleMouseMove = (event: Konva.KonvaPointerEvent) => {
    if (isPanToolActive) {
      return;
    }

    if (tool === "numbering" || tool === "draw-select") {
      drawSelectHandlers.onMouseMove(event);
      return;
    }

    if (
      (tool === "draw" || tool === "zone" || tool === "grid") &&
      drawTool.startPoint
    ) {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      const width = transformedPoint.x - drawTool.startPoint.x;
      const height = transformedPoint.y - drawTool.startPoint.y;

      // Calculate preview position based on drag direction
      const previewX = width < 0 ? transformedPoint.x : drawTool.startPoint.x;
      const previewY = height < 0 ? transformedPoint.y : drawTool.startPoint.y;

      // If shift key is pressed, make width and height equal
      let finalWidth = Math.abs(width);
      let finalHeight = Math.abs(height);

      if (event.evt.shiftKey && tool !== "grid") {
        const maxDimension = Math.max(finalWidth, finalHeight);
        finalWidth = maxDimension;
        finalHeight = maxDimension;
      }

      setPreviewShape({
        x: previewX,
        y: previewY,
        width: finalWidth,
        height: finalHeight,
        // The grid tool reuses the zone's dashed-rectangle preview look —
        // rows/columns aren't known until the GridPrompt panel appears, so
        // there's nothing more specific to show mid-drag.
        type:
          tool === "zone" || tool === "grid" ? "zone" : drawTool.selectedShape
      });

      return;
    }

    if (tool === "measure" && drawTool.startPoint) {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const transformed = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      setPreviewShape({
        x: drawTool.startPoint.x,
        y: drawTool.startPoint.y,
        width: 0,
        height: 0,
        type: "measure",
        x2: transformed.x,
        y2: transformed.y
      });

      return;
    }

    if (tool === "wall" && drawTool.startPoint) {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const raw = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      // Shift key: constrain to the nearest 0/45/90° angle from the start
      // point FIRST, then look for a wall to connect to along that
      // constrained line — so holding Shift keeps the preview straight
      // even close to another wall, instead of the connect-snap silently
      // overriding the angle constraint (matches handleMouseDown).
      let candidate = raw;
      if (event.evt.shiftKey) {
        const dx = raw.x - drawTool.startPoint.x;
        const dy = raw.y - drawTool.startPoint.y;
        const angle = Math.atan2(dy, dx);
        const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        const dist = Math.sqrt(dx * dx + dy * dy);
        candidate = {
          x: drawTool.startPoint.x + dist * Math.cos(snappedAngle),
          y: drawTool.startPoint.y + dist * Math.sin(snappedAngle)
        };
      }

      // Snap to existing wall endpoints or segment interiors.
      // Holding Alt bypasses wall snapping for free placement.
      const snapped: WallSnapResult = event.evt.altKey
        ? { ...candidate, snapped: false }
        : snapToWallEndpoint(
            candidate,
            shapes,
            stagePosition.scale,
            undefined,
            {
              includeSegments: true
            }
          );
      const endX = snapped.x;
      const endY = snapped.y;

      setPreviewShape({
        x: drawTool.startPoint.x,
        y: drawTool.startPoint.y,
        width: 0,
        height: 0,
        type: "wall",
        x2: endX,
        y2: endY,
        snapAlignX: snapped.alignX,
        snapAlignY: snapped.alignY,
        snapTarget:
          snapped.snapped &&
          snapped.alignX === undefined &&
          snapped.alignY === undefined
            ? { x: endX, y: endY }
            : undefined
      });

      return;
    }

    if (tool === "wall-path" && wallPathVerticesRef.current.length > 0) {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const raw = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      const existingVerts = wallPathVerticesRef.current;

      // Shift key: constrain to the nearest 0/45/90° angle from the last
      // vertex FIRST, then look for a wall to connect to along that
      // constrained line — so holding Shift keeps the preview straight
      // even close to another wall, instead of the connect-snap silently
      // overriding the angle constraint (matches handleMouseDown).
      let candidate = raw;
      if (event.evt.shiftKey && existingVerts.length > 0) {
        const last = existingVerts[existingVerts.length - 1];
        const dx = raw.x - last.x;
        const dy = raw.y - last.y;
        const angle = Math.atan2(dy, dx);
        const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        const dist = Math.sqrt(dx * dx + dy * dy);
        candidate = {
          x: last.x + dist * Math.cos(snappedAngle),
          y: last.y + dist * Math.sin(snappedAngle)
        };
      }

      // Snap to existing wall endpoints or segment interiors (same as wall
      // tool). Holding Alt bypasses wall snapping for free placement.
      const snapped: WallSnapResult = event.evt.altKey
        ? { ...candidate, snapped: false }
        : snapToWallEndpoint(
            candidate,
            shapes,
            stagePosition.scale,
            existingVerts,
            {
              includeSegments: true,
              excludeSegmentShapeId:
                wallPathActiveShapeIdRef.current ?? undefined
            }
          );
      const endX = snapped.x;
      const endY = snapped.y;

      setPreviewShape({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        type: "wall-path",
        x2: endX,
        y2: endY,
        vertices: existingVerts,
        snapAlignX: snapped.alignX,
        snapAlignY: snapped.alignY,
        snapTarget:
          snapped.snapped &&
          snapped.alignX === undefined &&
          snapped.alignY === undefined
            ? { x: endX, y: endY }
            : undefined
      });

      return;
    }

    if (tool === "wall-rect" && drawTool.startPoint) {
      const stage = event.target.getStage();
      const point = stage?.getPointerPosition();
      const raw = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      const x1 = drawTool.startPoint.x;
      const y1 = drawTool.startPoint.y;
      const x2 = raw.x;
      const y2 = raw.y;

      setPreviewShape({
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
        type: "wall-rect"
      });

      return;
    }

    if (isGhostItemEnabled) {
      updateIconToolGhostPosition();
      return;
    }

    if (!selectionStart || tool !== "select" || event.evt.altKey) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const transformed = {
      x: (pos.x - stagePosition.x) / stagePosition.scale,
      y: (pos.y - stagePosition.y) / stagePosition.scale
    };

    const dx = transformed.x - selectionStart.x;
    const dy = transformed.y - selectionStart.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > minSelectionDistance) {
      const newSelectionBox = {
        x: Math.min(selectionStart.x, transformed.x),
        y: Math.min(selectionStart.y, transformed.y),
        width: Math.abs(transformed.x - selectionStart.x),
        height: Math.abs(transformed.y - selectionStart.y)
      };

      setIsDragging(true);
      setSelectionBox(newSelectionBox);
      updateShapesInSelection(newSelectionBox);
    }
  };

  const handleMouseEnter = () => {
    setIsMouseIn(true);
  };

  const clearSelection = () => {
    setSelectionStart(null);
    setSelectionBox(null);
    setIsDragging(false);
    setShapesInSelection([]);
    isSelecting.current = false;
  };

  const handleMouseUp = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (isPanToolActive) {
      return;
    }

    if (e.type === "mouseleave") {
      setIsMouseIn(false);
    }

    if (tool === "grid" && drawTool.startPoint) {
      const stage = e.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      const width = Math.abs(transformedPoint.x - drawTool.startPoint.x);
      const height = Math.abs(transformedPoint.y - drawTool.startPoint.y);
      const x = Math.min(drawTool.startPoint.x, transformedPoint.x);
      const y = Math.min(drawTool.startPoint.y, transformedPoint.y);

      // "star" is a no-op shape type for handleDrawEnd — it only resets
      // drawTool.startPoint, same idiom as the measure tool.
      onDrawEnd(drawTool.startPoint, transformedPoint, "star");
      setPreviewShape(null);

      // Too small a drag to be an intentional grid — quietly ignore it
      // instead of popping the config dialog for an accidental click.
      if (width > 4 && height > 4) {
        setPendingGrid({ x, y, width, height });
      }
      return;
    }

    if ((tool === "draw" || tool === "zone") && drawTool.startPoint) {
      const stage = e.target.getStage();
      const point = stage?.getPointerPosition();
      const transformedPoint = snapPoint({
        x: ((point?.x || 0) - stagePosition.x) / stagePosition.scale,
        y: ((point?.y || 0) - stagePosition.y) / stagePosition.scale
      });

      // If shift key is pressed, ensure equal width and height
      if (e.evt.shiftKey) {
        const width = transformedPoint.x - drawTool.startPoint.x;
        const height = transformedPoint.y - drawTool.startPoint.y;

        const maxDimension = Math.max(Math.abs(width), Math.abs(height));

        if (Math.abs(width) > Math.abs(height)) {
          transformedPoint.y =
            drawTool.startPoint.y + (height < 0 ? -maxDimension : maxDimension);
        } else {
          transformedPoint.x =
            drawTool.startPoint.x + (width < 0 ? -maxDimension : maxDimension);
        }
      }

      const drawShape =
        tool === "zone" ? ("zone" as const) : drawTool.selectedShape;
      onDrawEnd(drawTool.startPoint, transformedPoint, drawShape);
      setPreviewShape(null);
      return;
    }

    if (tool === "numbering" || tool === "draw-select") {
      drawSelectHandlers.onMouseUp(e);
      return;
    }

    if (!selectionStart || tool !== "select") return;

    if (isDragging && selectionBox) {
      onSelectionChange(shapesInSelection);
      justFinishedDrag.current = true;
    }

    clearSelection();
  };

  const handleClick = React.useCallback(
    (event: Konva.KonvaPointerEvent) => {
      if (tool !== "select" || event.evt.altKey) return;

      // A drag selection just finished in mouseUp — skip the deselect
      if (justFinishedDrag.current) {
        justFinishedDrag.current = false;
        return;
      }

      const clickedTarget = getShapeRootTarget(
        shapes,
        event.target as Konva.Shape
      );

      const clickedOnShape =
        clickedTarget !== null && clickedTarget.locked !== true;

      if (!clickedOnShape) {
        onSelectionChange([]);
      }

      shouldPreventDeselect.current = false;
    },
    [onSelectionChange, tool]
  );

  const handleMultiDragStart = () => {
    const positions: { [key: string]: { x: number; y: number } } = {};
    selectedIdsRef.current.forEach((id) => {
      const shape = shapes.find((s) => s.id === id);
      if (shape) {
        positions[id] = { x: shape.x, y: shape.y };
      }
    });
    setDragStartPositions(positions);
  };

  const handleMultiDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {};

  const handleDragEnd = (event: Konva.KonvaEventObject<DragEvent>) => {
    const transformer = event.target;
    const dx = transformer.x();
    const dy = transformer.y();

    selectedIdsRef.current.forEach((id) => {
      const startPos = dragStartPositions[id];
      if (startPos) {
        onShapeUpdate(id, {
          x: startPos.x + dx,
          y: startPos.y + dy
        });
      }
    });
  };

  const handleMultiDragEnd = () => {
    setDragStartPositions({});
  };

  React.useEffect(() => {
    const message = "You have unsaved changes. Are you sure you want to leave?";
    // Both handlers read isDirtyRef live — without the gate, the browser
    // would prompt every time the tab tried to navigate (including auto
    // background activity), even after a clean save. That surfaced as a
    // loop of "leave page?" popups on idle tabs.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    // Push a dummy history entry so the browser back button triggers popstate
    // instead of immediately leaving the page.
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      if (!isDirtyRef.current) return;
      const leave = window.confirm(message);
      if (!leave) {
        // User cancelled — re-push so they stay on the page
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirtyRef]);

  // Stable handler refs — prevents Konva from detaching/reattaching
  // event listeners on every re-render of the parent component.
  const handlersRef = React.useRef({
    handleDragMove,
    handleDragEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseEnter,
    handleMouseUp,
    handleMultiDragStart,
    handleMultiDragMove,
    handleMultiDragEnd
  });
  handlersRef.current = {
    handleDragMove,
    handleDragEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseEnter,
    handleMouseUp,
    handleMultiDragStart,
    handleMultiDragMove,
    handleMultiDragEnd
  };

  const stableOnDragMove = React.useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) =>
      handlersRef.current.handleDragMove(e),
    []
  );
  const stableOnDragEnd = React.useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) =>
      handlersRef.current.handleDragEnd(e),
    []
  );
  const stableOnMouseDown = React.useCallback(
    (e: Konva.KonvaPointerEvent) => handlersRef.current.handleMouseDown(e),
    []
  );
  const stableOnMouseMove = React.useCallback(
    (e: Konva.KonvaPointerEvent) => handlersRef.current.handleMouseMove(e),
    []
  );
  const stableOnMouseEnter = React.useCallback(
    () => handlersRef.current.handleMouseEnter(),
    []
  );
  const stableOnMouseUp = React.useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) =>
      handlersRef.current.handleMouseUp(e),
    []
  );
  const stableMultiDragStart = React.useCallback(
    () => handlersRef.current.handleMultiDragStart(),
    []
  );
  const stableMultiDragMove = React.useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) =>
      handlersRef.current.handleMultiDragMove(e),
    []
  );
  const stableMultiDragEnd = React.useCallback(
    () => handlersRef.current.handleMultiDragEnd(),
    []
  );

  return {
    onWheel: handleWheel,
    onDragMove: stableOnDragMove,
    onDragEnd: stableOnDragEnd,
    onMouseDown: stableOnMouseDown,
    onMouseMove: stableOnMouseMove,
    onMouseEnter: stableOnMouseEnter,
    onMouseUp: stableOnMouseUp,
    onMouseLeave: stableOnMouseUp,
    onClick: handleClick,
    onTap: handleClick,
    handleMultiDragStart: stableMultiDragStart,
    handleMultiDragMove: stableMultiDragMove,
    handleMultiDragEnd: stableMultiDragEnd,
    isPanToolActive,
    isMouseIn,
    isDrawing,
    drawingPoints,
    cursor,
    pendingMeasurement,
    confirmMeasurement,
    cancelMeasurement,
    pendingGrid,
    confirmGrid,
    cancelGrid
  };
};
